import { useState, useMemo, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useQuery } from './useQuery';
import {
  calculateWeeklyOvertime,
  resolveWeeklyHours,
  type OvertimeBreakdown
} from '../utils/overtimeUtils';

// --- INTERFACES ---

export interface TimeEntryDetail {
  id: string;
  work_date: string;
  hours_normal: number;
  hours_saturday: number;
  hours_major: number;
  meals_count: number;
  travel_zone: string | number | null;
  indemnity_transport: number | null;
  indemnity_trajet: number | null;
  is_grand_deplacement: boolean | null;
  gd_amount: number | null;
  gd_travel_hours: number | null;
  /**
   * Description libre. Pour les imputations sur un projet "divers" (JLO/GP),
   * le builder pose ici le nom du sous-projet (ex: "Hôtel H24") afin de
   * conserver le client réel — sinon vide ou commentaire libre employé.
   */
  description: string | null;
  /** category: 'production' | 'divers' | ... — utile pour détecter les imputations divers. */
  project: { code: string; name: string; category?: string | null } | null;
  /** ID du sous-projet divers (JLO/GP) imputé, NULL pour les projets non-divers. */
  divers_sub_project_id?: string | null;
  /** Détails du sous-projet quand joint via select. Utilisé pour l'affichage. */
  divers_sub_project?: { id: string; name: string; divers_number?: string | null } | null;
}

export interface ProjectOption {
  id: string;
  code: string;
  name: string;
}

/**
 * Feuille hebdomadaire telle que retournée par le `.select()` de
 * `useValidateData` (jointures `employees` + `time_entries`).
 *
 * Miroir de la requête L61-67 :
 *   .select('*, employee:employees!employee_id(full_name, role, is_interim,
 *            custom_weekly_hours), time_entries(project_id)')
 *   .eq('status', 'pending')
 *
 * Exporté pour être réutilisé par useValidateActions et AdminValidate —
 * évite les `any` qui masquaient le typage strict du pipeline de validation.
 */
export interface PendingSheet {
  id: string;
  employee_id: string;
  year: number;
  week_number: number;
  status: string;
  week_start: string;
  total_hours: number;
  signature_url: string | null;
  pdf_url: string | null;
  signed_at: string | null;
  rejection_reason: string | null;
  rejected_at: string | null;
  override_weekly_hours: number | null;
  reminder_sent_at: string | null;
  validated_at: string | null;         // migration 021
  submitted_months: string[] | null;   // migration 015
  day_statuses: Record<string, 'draft' | 'submitted' | 'validated'> | null; // Phase 3
  correction_count: number;            // migration 019
  last_correction_at: string | null;   // migration 019
  last_correction_by: string | null;   // migration 019
  employee: {
    full_name: string;
    role: string;
    is_interim: boolean;
    custom_weekly_hours: number | null;
  } | null;
  time_entries: { project_id: string }[];
}

// --- UTILITAIRE WEEK -> DATE ---
const getWeekDate = (week: number, year: number) => {
  const simple = new Date(year, 0, 1 + (week - 1) * 7);
  const dow = simple.getDay();
  const ISOweekStart = simple;
  if (dow <= 4) ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
  else ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
  return ISOweekStart;
};

export function useValidateData() {
  // États de données
  const [selectedSheetId, setSelectedSheetId] = useState<string | null>(null);
  const [entries, setEntries] = useState<TimeEntryDetail[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  /**
   * Feuille chargée directement depuis la DB par loadSheetDetails. Sert de
   * fallback quand la feuille n'est pas dans pendingSheets/inProgressSheets
   * (cas : liste périmée, sélection par lien direct, race condition Realtime).
   * Sans ce fallback, currentSheet était undefined → AdminValidate rendait
   * une page blanche silencieuse (incident CORDIER S19, 18/05/2026).
   */
  const [fetchedSheet, setFetchedSheet] = useState<PendingSheet | null>(null);
  /** True si loadSheetDetails a tenté de fetcher mais que la feuille n'existe pas. */
  const [selectedSheetNotFound, setSelectedSheetNotFound] = useState(false);

  // Données chargées via useQuery
  const { data: projects } = useQuery<Pick<ProjectOption, 'id' | 'code' | 'name'>>(
    () => supabase
      .from('projects')
      .select('id, code, name')
      .eq('is_active', true)
      .order('code'),
    []
  );

  const { data: pendingSheets, loading, refresh: refreshSheets } = useQuery<PendingSheet>(
    () => supabase
      .from('weekly_sheets')
      .select('*, employee:employees!employee_id(full_name, role, is_interim, custom_weekly_hours), time_entries(project_id)')
      .eq('status', 'pending')
      .order('year', { ascending: false })
      .order('week_number', { ascending: false }),
    []
  );

  // Feuilles "En cours" : status='draft' avec au moins 1 jour 'validated'
  // = semaine à cheval où admin a validé un mois, en attente saisie du mois
  // suivant par l'employé. Utile pour que l'admin sache où en sont ces feuilles.
  const { data: inProgressSheetsRaw, refresh: refreshInProgress } = useQuery<PendingSheet>(
    () => supabase
      .from('weekly_sheets')
      .select('*, employee:employees!employee_id(full_name, role, is_interim, custom_weekly_hours), time_entries(project_id)')
      .eq('status', 'draft')
      .order('year', { ascending: false })
      .order('week_number', { ascending: false }),
    []
  );
  const inProgressSheets = useMemo(() =>
    inProgressSheetsRaw.filter(s =>
      Object.values(s.day_statuses || {}).some(v => v === 'validated')
    ),
    [inProgressSheetsRaw]
  );

  // État override
  const [overrideLoading, setOverrideLoading] = useState(false);

  // État feedback zone (checkmark vert après sauvegarde)
  const [zoneSavedEntryId, setZoneSavedEntryId] = useState<string | null>(null);
  const [savedFieldId, setSavedFieldId] = useState<string | null>(null);

  // États des filtres
  const [filterName, setFilterName] = useState('');
  const [filterWeek, setFilterWeek] = useState('all');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterProject, setFilterProject] = useState('all');

  // Chargement du détail d'une feuille (réutilisé pour le refetch Realtime)
  // Fetch les entries ET la feuille elle-même depuis la DB. Avant ce fix,
  // currentSheet venait UNIQUEMENT de la mémoire (pendingSheets/inProgressSheets),
  // ce qui causait une page blanche si la liste était périmée.
  const loadSheetDetails = useCallback(async (sheetId: string) => {
    setDetailLoading(true);
    setSelectedSheetId(sheetId);
    setSelectedSheetNotFound(false);

    // Fetch en parallèle : entries + feuille elle-même (avec employee joined)
    const [entriesRes, sheetRes] = await Promise.all([
      supabase
        .from('time_entries')
        .select('*, project:projects(code, name, category), divers_sub_project:divers_sub_projects(id, name, divers_number)')
        .eq('sheet_id', sheetId)
        .order('work_date', { ascending: true }),
      supabase
        .from('weekly_sheets')
        .select('*, employee:employees!employee_id(full_name, role, is_interim, custom_weekly_hours), time_entries(project_id)')
        .eq('id', sheetId)
        .maybeSingle(),
    ]);

    if (entriesRes.error) console.error('[loadSheetDetails] entries error:', entriesRes.error);
    if (sheetRes.error) console.error('[loadSheetDetails] sheet error:', sheetRes.error);

    setEntries((entriesRes.data ?? []) as unknown as TimeEntryDetail[]);

    if (sheetRes.data) {
      setFetchedSheet(sheetRes.data as unknown as PendingSheet);
      setSelectedSheetNotFound(false);
    } else {
      setFetchedSheet(null);
      setSelectedSheetNotFound(true);
    }

    setDetailLoading(false);
  }, []);

  // --- REALTIME SYNC ---
  // Rafraîchit automatiquement les données quand :
  // - une time_entry de la feuille sélectionnée est modifiée (par un autre admin, le secrétariat, etc.)
  // - la feuille elle-même change de statut ou d'override_weekly_hours
  useEffect(() => {
    if (!selectedSheetId) return;

    const channel = supabase
      .channel(`validate-sheet-${selectedSheetId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'time_entries',
          filter: `sheet_id=eq.${selectedSheetId}`,
        },
        () => {
          // Refetch complet pour récupérer le join projects
          loadSheetDetails(selectedSheetId);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'weekly_sheets',
          filter: `id=eq.${selectedSheetId}`,
        },
        () => {
          // La feuille elle-même a changé (status, override_weekly_hours, correction_count...)
          refreshSheets();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSheetId]);

  // currentSheet : cherche dans les deux listes, puis fallback sur la feuille
  // fetchée directement par loadSheetDetails (cas listes périmées ou hors
  // critères de filtrage). Garantit que currentSheet n'est jamais undefined
  // si la feuille existe en DB et a été chargée.
  const currentSheet =
    pendingSheets.find(s => s.id === selectedSheetId)
    ?? inProgressSheets.find(s => s.id === selectedSheetId)
    ?? (fetchedSheet && fetchedSheet.id === selectedSheetId ? fetchedSheet : undefined);

  // --- CALCULS TOTAUX (CORRECTIFS DÉDOUBLONNAGE) ---
  const totalHours = parseFloat(entries.reduce((acc, curr) => acc + (curr.hours_normal || 0) + (curr.hours_saturday || 0) + (curr.hours_major || 0), 0).toFixed(2));

  const processedDays = new Set<string>();
  const processedGDDays = new Set<string>();
  let totalMeals = 0;
  let totalTransport = 0;
  let totalTrajet = 0;
  let totalGDDays = 0;

  // Calcul des heures par type pour les HS
  let normalHoursTotal = 0;
  let saturdayHoursTotal = 0;
  let sundayHoursTotal = 0;

  entries.forEach(e => {
    normalHoursTotal += (e.hours_normal || 0);
    saturdayHoursTotal += (e.hours_saturday || 0);
    sundayHoursTotal += (e.hours_major || 0);

    if (!processedDays.has(e.work_date)) {
      if (!e.is_grand_deplacement) {
        totalMeals += (e.meals_count || 0);
        totalTransport += (e.indemnity_transport || 0);
        totalTrajet += (e.indemnity_trajet || 0);
      }
      processedDays.add(e.work_date);
    }

    if (e.is_grand_deplacement && !processedGDDays.has(e.work_date)) {
      totalGDDays += 1;
      processedGDDays.add(e.work_date);
    }
  });

  // Déterminer si la semaine contient des jours en Grand Déplacement
  const hasGD = entries.some(e => e.is_grand_deplacement === true);

  // Calcul des heures supplémentaires
  const isBE35hValidate = currentSheet?.employee?.role === 'bureau_etude';
  const customHoursValidate = resolveWeeklyHours(currentSheet?.override_weekly_hours, currentSheet?.employee?.custom_weekly_hours);
  const overtimeBreakdown: OvertimeBreakdown | null = useMemo(() => {
    if (!currentSheet || entries.length === 0) return null;
    const hoursLuSa = normalHoursTotal + saturdayHoursTotal;
    return calculateWeeklyOvertime(currentSheet.week_number, hoursLuSa, sundayHoursTotal, hasGD || !!isBE35hValidate, customHoursValidate);
  }, [currentSheet, entries, normalHoursTotal, saturdayHoursTotal, sundayHoursTotal, hasGD, isBE35hValidate, customHoursValidate]);

  // --- FILTRAGE ---
  const filteredSheets = useMemo(() => {
    return pendingSheets.filter(sheet => {
      const nameMatch = (sheet.employee?.full_name || '').toLowerCase().includes(filterName.toLowerCase());
      if (!nameMatch) return false;
      if (filterWeek !== 'all' && sheet.week_number !== parseInt(filterWeek)) return false;
      if (filterMonth) {
        const sheetDate = getWeekDate(sheet.week_number, sheet.year);
        const sheetMonthStr = sheetDate.toISOString().slice(0, 7);
        if (sheetMonthStr !== filterMonth) return false;
      }
      if (filterProject !== 'all') {
        const hasProject = sheet.time_entries?.some((te: { project_id: string }) => te.project_id === filterProject);
        if (!hasProject) return false;
      }
      return true;
    });
  }, [pendingSheets, filterName, filterWeek, filterMonth, filterProject]);

  /**
   * Wrap setSelectedSheetId : quand on revient à la liste (null), on nettoie
   * aussi le fetchedSheet et l'état not-found pour éviter d'afficher des
   * informations périmées au prochain clic.
   */
  const setSelectedSheetIdWrapped = useCallback((id: string | null) => {
    setSelectedSheetId(id);
    if (id === null) {
      setFetchedSheet(null);
      setSelectedSheetNotFound(false);
      setEntries([]);
    }
  }, []);

  return {
    // Sheet selection / details
    selectedSheetId,
    setSelectedSheetId: setSelectedSheetIdWrapped,
    entries,
    setEntries,
    loadSheetDetails,
    currentSheet,
    /** True si on a cliqué sur une feuille qui n'existe pas (ou plus) en DB. */
    selectedSheetNotFound,
    /** True pendant le fetch des détails d'une feuille sélectionnée. */
    detailLoading,
    // Queries
    projects,
    pendingSheets,
    inProgressSheets,
    refreshInProgress,
    loading,
    refreshSheets,
    // Override loading
    overrideLoading,
    setOverrideLoading,
    // Feedback states
    zoneSavedEntryId,
    setZoneSavedEntryId,
    savedFieldId,
    setSavedFieldId,
    // Filters
    filterName,
    setFilterName,
    filterWeek,
    setFilterWeek,
    filterMonth,
    setFilterMonth,
    filterProject,
    setFilterProject,
    filteredSheets,
    // Computed totals
    totalHours,
    totalMeals,
    totalTransport,
    totalTrajet,
    totalGDDays,
    normalHoursTotal,
    saturdayHoursTotal,
    sundayHoursTotal,
    hasGD,
    overtimeBreakdown,
  };
}
